import { useEffect, useMemo, useState } from "react";
import {
  useCreateFrame,
  useDeleteFrame,
  useDeleteFrameParameter,
  useFlights,
  useFrameDetail,
  useFrameParameter,
  useFrames,
  useImportPrm,
  useSaveBindings,
  useSaveFrameParameter,
  useUpdateFrameMeta,
} from "../../api/hooks";
import type { FrameBinding, FrameRow, ParameterDef } from "../../api/types";
import { useSession } from "../../app/store";
import { Split } from "../../components/Split";
import { Empty, Modal, Spinner, errText, fmtBytes } from "../../components/ui";
import { ParameterEditor, blankParameter } from "./ParameterEditor";
import "./frame.css";

type SortKey = "mnemonic" | "rate" | "words" | "origin" | "units";

export function FramePage() {
  const { projectId, activeFlight, toast } = useSession();
  const frames = useFrames(projectId);
  const flights = useFlights();
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [selectedParam, setSelectedParam] = useState<string | null>(null);
  const [draft, setDraft] = useState<ParameterDef | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [filter, setFilter] = useState("");
  const [originFilter, setOriginFilter] = useState<"all" | "prm" | "manual" | "override">("all");
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "mnemonic", dir: 1 });
  const [dialog, setDialog] = useState<"import" | "new" | "bindings" | "meta" | null>(null);

  const detail = useFrameDetail(projectId, selectedFile);
  const param = useFrameParameter(projectId, selectedFile, isNew ? null : selectedParam);
  const saveParam = useSaveFrameParameter(projectId);
  const delParam = useDeleteFrameParameter(projectId);
  const delFrame = useDeleteFrame(projectId);

  useEffect(() => {
    if (!frames.data) return;
    if (selectedFile && frames.data.frames.some((f) => f.name === selectedFile)) return;
    const def = frames.data.frames.find((f) => f.path === frames.data?.default) ?? frames.data.frames[0];
    setSelectedFile(def?.name ?? null);
    setSelectedParam(null);
    setDraft(null);
  }, [frames.data, selectedFile]);

  // load the full definition into the editor when a row is picked
  useEffect(() => {
    if (isNew) return;
    if (param.data) setDraft({ ...blankParameter(), ...param.data.parameter });
  }, [param.data, isNew]);

  const rows = useMemo(() => {
    const all = detail.data?.parameters ?? [];
    const q = filter.trim().toLowerCase();
    const filtered = all.filter(
      (r) =>
        (originFilter === "all" || r.origin === originFilter) &&
        (!q || r.mnemonic.toLowerCase().includes(q) || (r.name ?? "").toLowerCase().includes(q) || (r.units ?? "").toLowerCase().includes(q) || r.words.some((w) => String(w) === q)),
    );
    const cmp = (a: FrameRow, b: FrameRow) => {
      switch (sort.key) {
        case "rate":
          return a.rate - b.rate;
        case "words":
          return (a.words[0] ?? 0) - (b.words[0] ?? 0);
        case "origin":
          return a.origin.localeCompare(b.origin);
        case "units":
          return (a.units ?? "").localeCompare(b.units ?? "");
        default:
          return a.mnemonic.localeCompare(b.mnemonic);
      }
    };
    return filtered.sort((a, b) => sort.dir * cmp(a, b));
  }, [detail.data, filter, originFilter, sort]);

  const flightLabel = useMemo(() => {
    const f = flights.data?.find((x) => x.id === activeFlight);
    return f ? `${f.tail ?? "?"} ${f.date?.slice(0, 10) ?? ""}` : "no active flight";
  }, [flights.data, activeFlight]);

  const counts = useMemo(() => {
    const c = { prm: 0, manual: 0, override: 0 };
    for (const r of detail.data?.parameters ?? []) c[r.origin]++;
    return c;
  }, [detail.data]);

  const pick = (m: string) => {
    setIsNew(false);
    setSelectedParam(m);
  };
  const startNew = () => {
    setIsNew(true);
    setSelectedParam(null);
    setDraft(blankParameter());
  };
  const closeEditor = () => {
    setIsNew(false);
    setSelectedParam(null);
    setDraft(null);
  };

  const doSave = () => {
    if (!draft || !selectedFile) return;
    const renameFrom = !isNew && selectedParam && selectedParam !== draft.mnemonic ? selectedParam : undefined;
    saveParam.mutate(
      { frame: selectedFile, mnemonic: draft.mnemonic, parameter: draft, renameFrom },
      {
        onSuccess: (r) => {
          toast(`${r.parameter.mnemonic} saved (${r.origin})`, "success");
          setIsNew(false);
          setSelectedParam(r.parameter.mnemonic);
        },
        onError: (e) => toast(errText(e), "error"),
      },
    );
  };
  const doRevert = () => {
    if (!selectedFile || !selectedParam) return;
    const isOverride = param.data?.is_override ?? false;
    if (!confirm(isOverride ? `Revert ${selectedParam} to the PRM definition?` : `Delete ${selectedParam}?`)) return;
    const mnemonic = selectedParam;
    // a manual parameter disappears: stop observing its definition before the cache is invalidated
    if (!isOverride) closeEditor();
    delParam.mutate(
      { frame: selectedFile, mnemonic },
      {
        onSuccess: (r) => {
          toast(r.reverted_to ? `${mnemonic} reverted` : `${mnemonic} deleted`, "success");
          if (r.deleted) closeEditor();
        },
        onError: (e) => {
          toast(errText(e), "error");
          if (!isOverride) pick(mnemonic);
        },
      },
    );
  };

  if (!projectId) return <Empty title="No project selected" />;

  const current = frames.data?.frames.find((f) => f.name === selectedFile);
  const sortBtn = (key: SortKey, label: string) => (
    <th className="clickable" onClick={() => setSort((s) => ({ key, dir: s.key === key ? ((s.dir * -1) as 1 | -1) : 1 }))}>
      {label}
      {sort.key === key ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
    </th>
  );

  return (
    <div className="page frame-page">
      <div className="page-head">
        <h1>Frame</h1>
        <span className="sub">Raw ARINC 717 words → engineering units. PRM parameters can be overridden; manual parameters are added beside them.</span>
        <span className="spacer" />
        <button className="btn" onClick={() => setDialog("import")}>
          ⇪ Import PRM
        </button>
        <button className="btn" onClick={() => setDialog("new")}>
          ＋ Manual frame
        </button>
        <button className="btn" onClick={() => setDialog("bindings")} title="Which frame decodes which tails">
          Tail bindings
        </button>
      </div>
      <div className="page-body">
        <Split id="frame" sizes={[220, "1fr", 460]} minSizes={[140, 320, 300]}>
          <div className="pane">
            <div className="pane-head">Frame files</div>
            <div className="list">
              {frames.data?.frames.map((f) => (
                <div
                  key={f.name}
                  className={`list-item${f.name === selectedFile ? " sel" : ""}`}
                  onClick={() => {
                    setSelectedFile(f.name);
                    closeEditor();
                  }}
                  title={f.error ?? f.path}
                >
                  <div className="col" style={{ gap: 2, minWidth: 0 }}>
                    <span className="name mono">{f.name}</span>
                    <span className="row small dim wrap">
                      <span className={`badge ${f.kind === "prm" ? "" : "info"}`}>{f.kind}</span>
                      {f.is_default ? <span className="badge ok">default</span> : null}
                      {f.bindings.length ? <span className="badge">{f.bindings.flatMap((b) => b.tails).join(", ")}</span> : null}
                      {f.overrides ? <span className="badge warn">{f.overrides} edits</span> : null}
                      {f.error ? <span className="badge err">error</span> : null}
                    </span>
                    <span className="faint small">
                      {f.parameter_count ?? "?"} params · {f.words_per_second ?? "?"} wps · {fmtBytes(f.size)}
                    </span>
                  </div>
                </div>
              ))}
              {frames.data && !frames.data.frames.length ? (
                <div className="empty small">
                  No frame yet. Import a PRM or create a manual frame.
                </div>
              ) : null}
            </div>
          </div>

          <div className="pane">
            <div className="pane-head">
              <span className="mono">{detail.data?.frame.name ?? selectedFile ?? "—"}</span>
              {detail.data ? (
                <>
                  <span className="badge">{detail.data.frame.words_per_second ?? "?"} wps</span>
                  {detail.data.frame.aircraft_manufacturer || detail.data.frame.aircraft_model ? (
                    <span className="dim small">
                      {[detail.data.frame.aircraft_manufacturer, detail.data.frame.aircraft_model].filter(Boolean).join(" ")}
                    </span>
                  ) : null}
                  {detail.data.superframe_counter ? (
                    <span className="badge info" title="superframe counter parameter">
                      SF: {detail.data.superframe_counter}
                    </span>
                  ) : null}
                  {detail.data.problems.length ? (
                    <span className="badge err" title={detail.data.problems.join("\n")}>
                      {detail.data.problems.length} problems
                    </span>
                  ) : null}
                </>
              ) : null}
              <span className="spacer" />
              {detail.data?.kind === "yaml" ? (
                <button className="btn sm ghost" onClick={() => setDialog("meta")}>
                  Edit header
                </button>
              ) : null}
              {current ? (
                <button
                  className="btn sm ghost danger"
                  onClick={() => {
                    if (confirm(`Delete ${current.name}${current.overrides ? " and its overrides" : ""}?`))
                      delFrame.mutate(current.name, { onSuccess: () => toast("Frame deleted", "success"), onError: (e) => toast(errText(e), "error") });
                  }}
                >
                  Delete
                </button>
              ) : null}
            </div>
            <div className="toolbar row">
              <input className="inp sm" placeholder="Filter mnemonic, name, units or word…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 280 }} />
              <div className="tabs sm">
                {(["all", "prm", "manual", "override"] as const).map((o) => (
                  <button key={o} className={originFilter === o ? "on" : ""} onClick={() => setOriginFilter(o)}>
                    {o}
                    {o !== "all" ? <span className="faint"> {counts[o]}</span> : null}
                  </button>
                ))}
              </div>
              <span className="spacer" />
              <span className="dim small">
                {rows.length} / {detail.data?.parameters.length ?? 0}
              </span>
              <button className="btn sm primary" disabled={!selectedFile} onClick={startNew}>
                ＋ Parameter
              </button>
            </div>
            <div className="pane-body" style={{ padding: 0 }}>
              {detail.isLoading ? <Spinner label="loading frame…" /> : null}
              {detail.error ? <div className="err small" style={{ padding: 12 }}>{errText(detail.error)}</div> : null}
              {detail.data ? (
                <table className="tbl param-tbl">
                  <thead>
                    <tr>
                      {sortBtn("mnemonic", "Mnemonic")}
                      <th>Name</th>
                      {sortBtn("units", "Units")}
                      {sortBtn("rate", "Hz")}
                      {sortBtn("words", "Words")}
                      <th>Bits</th>
                      <th>Function</th>
                      <th>SF</th>
                      {sortBtn("origin", "Origin")}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.mnemonic} className={`clickable${r.mnemonic === selectedParam ? " sel" : ""}`} onClick={() => pick(r.mnemonic)}>
                        <td className="mono">{r.mnemonic}</td>
                        <td className="dim">{r.name ?? ""}</td>
                        <td>{r.units ?? ""}</td>
                        <td className="num">{r.rate}</td>
                        <td className="mono small">
                          {r.words.join(",")}
                          {r.lsb != null ? <span className="faint"> [{r.lsb}-{r.msb}]</span> : null}
                          {r.subframes && r.subframes.length < 4 ? <span className="faint"> sf{r.subframes.join("")}</span> : null}
                        </td>
                        <td className="num">{r.bits}</td>
                        <td className="small">
                          {r.function}
                          {r.function === "Linear" && (r.coefficients[0] !== 0 || r.coefficients[1] !== 1) ? (
                            <span className="faint mono"> {r.coefficients.slice(0, 2).join(",")}</span>
                          ) : null}
                        </td>
                        <td className="num">{r.superframe ?? ""}</td>
                        <td>
                          <span className={`badge ${r.origin === "override" ? "warn" : r.origin === "manual" ? "info" : ""}`}>{r.origin}</span>
                        </td>
                      </tr>
                    ))}
                    {!rows.length ? (
                      <tr>
                        <td colSpan={9} className="dim small" style={{ textAlign: "center", padding: 24 }}>
                          No parameters match.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              ) : null}
            </div>
          </div>

          {draft && selectedFile ? (
            <div className="pane" style={{ overflow: "hidden" }}>
              <ParameterEditor
                key={isNew ? "__new__" : selectedParam ?? ""}
                projectId={projectId}
                frameName={selectedFile}
                wordsPerSecond={detail.data?.frame.words_per_second}
                value={draft}
                original={param.data?.original ?? null}
                isOverride={param.data?.is_override ?? false}
                isNew={isNew}
                flightId={activeFlight}
                flightLabel={flightLabel}
                saving={saveParam.isPending}
                onChange={setDraft}
                onSave={doSave}
                onRevert={doRevert}
                onCancel={closeEditor}
              />
            </div>
          ) : null}
        </Split>
      </div>

      {dialog === "import" ? <ImportPrmDialog projectId={projectId} onClose={() => setDialog(null)} onDone={(name) => (setSelectedFile(name), setDialog(null))} /> : null}
      {dialog === "new" ? (
        <NewFrameDialog projectId={projectId} frames={frames.data?.frames.map((f) => f.name) ?? []} onClose={() => setDialog(null)} onDone={(name) => (setSelectedFile(name), setDialog(null))} />
      ) : null}
      {dialog === "bindings" && frames.data ? (
        <BindingsDialog
          projectId={projectId}
          frames={frames.data.frames.map((f) => f.path)}
          defaultFrame={frames.data.default}
          bindings={frames.data.bindings}
          tails={[...new Set((flights.data ?? []).map((f) => f.tail).filter(Boolean) as string[])]}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "meta" && detail.data ? <MetaDialog projectId={projectId} frame={detail.data.name} meta={detail.data.frame} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}

// --------------------------------------------------------------------------------------------
// dialogs
// --------------------------------------------------------------------------------------------

function ImportPrmDialog({ projectId, onClose, onDone }: { projectId: string; onClose: () => void; onDone: (name: string) => void }) {
  const imp = useImportPrm(projectId);
  const toast = useSession((s) => s.toast);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [tails, setTails] = useState("");
  const [setDefault, setSetDefault] = useState(true);
  const [overwrite, setOverwrite] = useState(false);
  return (
    <Modal
      title="Import PRM"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!file || imp.isPending}
            onClick={() =>
              file &&
              imp.mutate(
                { file, name: name || undefined, setDefault, tails: tails.split(",").map((t) => t.trim()).filter(Boolean), overwrite },
                {
                  onSuccess: (r) => {
                    toast(`${r.name}: ${r.parameter_count} parameters${r.problems.length ? `, ${r.problems.length} problems` : ""}`, "success");
                    onDone(r.name);
                  },
                  onError: (e) => toast(errText(e), "error"),
                },
              )
            }
          >
            Import
          </button>
        </>
      }
    >
      <label className="field">
        <span>PRM file</span>
        <input type="file" accept=".prm,.txt" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </label>
      <label className="field">
        <span>Name (file stem; defaults to the upload name)</span>
        <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder={file?.name.replace(/\.prm$/i, "") ?? "B737_fleet"} />
      </label>
      <label className="field">
        <span>Bind to tails (comma separated, optional)</span>
        <input className="inp" value={tails} onChange={(e) => setTails(e.target.value)} placeholder="XA-HPY, XA-HPZ" />
      </label>
      <label className="row small" style={{ gap: 6 }}>
        <input type="checkbox" checked={setDefault} onChange={(e) => setSetDefault(e.target.checked)} /> make it the project default frame
      </label>
      <label className="row small" style={{ gap: 6 }}>
        <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} /> overwrite if a frame with this name exists
      </label>
      <p className="dim small" style={{ margin: 0 }}>
        The PRM is stored exactly as imported; your edits live in a sidecar (<code>.overrides.yaml</code>) so the import can be refreshed later without losing them.
      </p>
    </Modal>
  );
}

function NewFrameDialog({ projectId, frames, onClose, onDone }: { projectId: string; frames: string[]; onClose: () => void; onDone: (name: string) => void }) {
  const create = useCreateFrame(projectId);
  const toast = useSession((s) => s.toast);
  const [name, setName] = useState("");
  const [wps, setWps] = useState(256);
  const [copyFrom, setCopyFrom] = useState("");
  const [setDefault, setSetDefault] = useState(false);
  return (
    <Modal
      title="New manual frame"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!name.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                { name, words_per_second: wps, copy_from: copyFrom || undefined, set_default: setDefault },
                { onSuccess: (r) => onDone(r.name), onError: (e) => toast(errText(e), "error") },
              )
            }
          >
            Create
          </button>
        </>
      }
    >
      <label className="field">
        <span>Name</span>
        <input className="inp" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="b737_manual" />
      </label>
      <label className="field">
        <span>Words per second</span>
        <select className="inp" value={wps} onChange={(e) => setWps(Number(e.target.value))}>
          {[64, 128, 256, 512, 1024].map((w) => (
            <option key={w} value={w}>
              {w}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Copy parameters from</span>
        <select className="inp" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
          <option value="">— start empty —</option>
          {frames.map((f) => (
            <option key={f}>{f}</option>
          ))}
        </select>
      </label>
      <label className="row small" style={{ gap: 6 }}>
        <input type="checkbox" checked={setDefault} onChange={(e) => setSetDefault(e.target.checked)} /> make it the default frame
      </label>
    </Modal>
  );
}

function BindingsDialog({
  projectId,
  frames,
  defaultFrame,
  bindings,
  tails,
  onClose,
}: {
  projectId: string;
  frames: string[];
  defaultFrame: string | null;
  bindings: FrameBinding[];
  tails: string[];
  onClose: () => void;
}) {
  const save = useSaveBindings(projectId);
  const toast = useSession((s) => s.toast);
  const [def, setDef] = useState<string>(defaultFrame ?? frames[0] ?? "");
  const [rows, setRows] = useState<{ frame: string; tails: string }[]>(bindings.map((b) => ({ frame: b.frame, tails: b.tails.join(", ") })));
  return (
    <Modal
      title="Tail bindings"
      width={560}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  default: def || null,
                  bindings: rows
                    .filter((r) => r.frame)
                    .map((r) => ({ frame: r.frame, tails: r.tails.split(",").map((t) => t.trim()).filter(Boolean) || ["*"] }))
                    .map((r) => ({ ...r, tails: r.tails.length ? r.tails : ["*"] })),
                },
                { onSuccess: () => (toast("Bindings saved", "success"), onClose()), onError: (e) => toast(errText(e), "error") },
              )
            }
          >
            Save
          </button>
        </>
      }
    >
      <label className="field">
        <span>Default frame (used when no binding matches the tail)</span>
        <select className="inp" value={def} onChange={(e) => setDef(e.target.value)}>
          <option value="">— none —</option>
          {frames.map((f) => (
            <option key={f}>{f}</option>
          ))}
        </select>
      </label>
      <table className="tbl">
        <thead>
          <tr>
            <th>Frame</th>
            <th>Tails (comma separated, * = any)</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                <select className="inp sm" value={r.frame} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, frame: e.target.value } : x)))}>
                  {frames.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </td>
              <td>
                <input className="inp sm mono" style={{ width: "100%" }} value={r.tails} list="known-tails" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, tails: e.target.value } : x)))} />
              </td>
              <td>
                <button className="btn sm ghost danger" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <datalist id="known-tails">
        {tails.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      <button className="btn sm" onClick={() => setRows([...rows, { frame: frames[0] ?? "", tails: "" }])} disabled={!frames.length}>
        ＋ binding
      </button>
      <p className="dim small" style={{ margin: 0 }}>
        First matching binding wins. Known tails: {tails.length ? tails.join(", ") : "none registered yet"}.
      </p>
    </Modal>
  );
}

function MetaDialog({
  projectId,
  frame,
  meta,
  onClose,
}: {
  projectId: string;
  frame: string;
  meta: { name: string; description?: string | null; words_per_second?: number | null; aircraft_manufacturer?: string | null; aircraft_model?: string | null };
  onClose: () => void;
}) {
  const upd = useUpdateFrameMeta(projectId);
  const toast = useSession((s) => s.toast);
  const [m, setM] = useState({
    name: meta.name,
    description: meta.description ?? "",
    words_per_second: meta.words_per_second ?? 256,
    aircraft_manufacturer: meta.aircraft_manufacturer ?? "",
    aircraft_model: meta.aircraft_model ?? "",
  });
  return (
    <Modal
      title={`Frame header – ${frame}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={upd.isPending}
            onClick={() => upd.mutate({ frame, ...m }, { onSuccess: () => (toast("Saved", "success"), onClose()), onError: (e) => toast(errText(e), "error") })}
          >
            Save
          </button>
        </>
      }
    >
      <label className="field">
        <span>Name</span>
        <input className="inp" value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} />
      </label>
      <label className="field">
        <span>Description</span>
        <input className="inp" value={m.description} onChange={(e) => setM({ ...m, description: e.target.value })} />
      </label>
      <label className="field">
        <span>Words per second</span>
        <input className="inp mono" type="number" value={m.words_per_second} onChange={(e) => setM({ ...m, words_per_second: Number(e.target.value) })} />
      </label>
      <div className="grid2">
        <label className="field">
          <span>Manufacturer</span>
          <input className="inp" value={m.aircraft_manufacturer} onChange={(e) => setM({ ...m, aircraft_manufacturer: e.target.value })} />
        </label>
        <label className="field">
          <span>Model</span>
          <input className="inp" value={m.aircraft_model} onChange={(e) => setM({ ...m, aircraft_model: e.target.value })} />
        </label>
      </div>
    </Modal>
  );
}
